import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, Button, Badge } from '../components/Common';

const API_URL = process.env.REACT_APP_API_URL || 'https://physio-scheduler-api.onrender.com/api';

export function Account() {
  const { user, requestLicense, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordResult, setPasswordResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestResult, setRequestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordResult(null);

    if (newPassword !== confirmPassword) {
      setPasswordResult({ success: false, message: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setPasswordResult({ success: false, message: 'New password must be at least 6 characters' });
      return;
    }

    setIsChangingPassword(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();

      if (!res.ok) {
        const msg = typeof data.error === 'string' ? data.error : data.error?.message || 'Failed';
        throw new Error(msg);
      }

      setPasswordResult({ success: true, message: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleRequestLicense = async (type: 'trial' | 'permanent') => {
    setIsRequesting(true);
    setRequestResult(null);
    try {
      await requestLicense(type);
      setRequestResult({ success: true, message: `${type === 'trial' ? 'Trial' : 'Permanent'} licence request submitted. An admin will review it shortly.` });
    } catch (err) {
      setRequestResult({ success: false, message: err instanceof Error ? err.message : 'Request failed' });
    } finally {
      setIsRequesting(false);
    }
  };

  // Determine licence status display
  const renderLicenceStatus = () => {
    if (!user) return null;

    // Admin bypasses
    if (user.role === 'admin') {
      return (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-green-800">Admin Account</h4>
              <p className="text-sm text-green-600 mt-1">Full access to all features.</p>
            </div>
            <Badge variant="success">Admin</Badge>
          </div>
        </div>
      );
    }

    // Has pending request
    if (user.license_requested_at) {
      return (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-amber-800">Licence Request Pending</h4>
              <p className="text-sm text-amber-600 mt-1">
                You have requested a <strong>{user.license_requested_type}</strong> licence. An admin will review your request shortly.
              </p>
              <p className="text-xs text-amber-500 mt-1">
                Requested: {new Date(user.license_requested_at).toLocaleDateString()}
              </p>
            </div>
            <Badge variant="warning">Pending</Badge>
          </div>
        </div>
      );
    }

    // Active trial
    if (user.license_type === 'trial' && user.license_status === 'active') {
      return (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-blue-800">Trial Licence</h4>
              <p className="text-sm text-blue-600 mt-1">
                Your trial licence is active.
                {user.license_expires_at && (
                  <> Expires: <strong>{new Date(user.license_expires_at).toLocaleDateString()}</strong></>
                )}
              </p>
            </div>
            <Badge variant="info">Trial</Badge>
          </div>
          <div className="mt-3">
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleRequestLicense('permanent')}
              isLoading={isRequesting}
            >
              Request Permanent Licence
            </Button>
          </div>
        </div>
      );
    }

    // Active permanent
    if (user.license_type === 'permanent' && user.license_status === 'active') {
      return (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-green-800">Permanent Licence</h4>
              <p className="text-sm text-green-600 mt-1">Your permanent licence is active.</p>
            </div>
            <Badge variant="success">Active</Badge>
          </div>
        </div>
      );
    }

    // Expired or revoked
    if (user.license_status === 'expired' || user.license_status === 'revoked') {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-red-800">
                Licence {user.license_status === 'expired' ? 'Expired' : 'Revoked'}
              </h4>
              <p className="text-sm text-red-600 mt-1">
                Your licence is no longer active. Please request a new one below.
              </p>
            </div>
            <Badge variant="danger">{user.license_status === 'expired' ? 'Expired' : 'Revoked'}</Badge>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleRequestLicense('trial')}
              isLoading={isRequesting}
            >
              Request Trial
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleRequestLicense('permanent')}
              isLoading={isRequesting}
            >
              Request Permanent
            </Button>
          </div>
        </div>
      );
    }

    // No licence — default state for new users
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-800">No Active Licence</h4>
            <p className="text-sm text-gray-600 mt-1">
              You need a licence to access the scheduler. Request one below and an admin will review it.
            </p>
          </div>
          <Badge variant="warning">No Licence</Badge>
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleRequestLicense('trial')}
            isLoading={isRequesting}
          >
            Request Trial (14 days)
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleRequestLicense('permanent')}
            isLoading={isRequesting}
          >
            Request Permanent Licence
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">My Account</h1>

      {/* Profile */}
      <Card title="Profile">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Name</label>
            <p className="text-sm text-gray-900">{user?.name}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Email</label>
            <p className="text-sm text-gray-900">{user?.email}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Role</label>
            <p className="text-sm text-gray-900 capitalize">{user?.role}</p>
          </div>
        </div>
      </Card>

      {/* Licence Status */}
      <Card title="Licence">
        <div className="space-y-3">
          {renderLicenceStatus()}

          {requestResult && (
            <div
              className={`p-3 rounded-lg text-sm ${
                requestResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <p>{requestResult.message}</p>
                <button onClick={() => setRequestResult(null)} className="text-current opacity-50">
                  &times;
                </button>
              </div>
            </div>
          )}

          <button
            onClick={refreshUser}
            className="text-sm text-primary-600 hover:text-primary-800 underline"
          >
            Refresh licence status
          </button>
        </div>
      </Card>

      {/* Change Password */}
      <Card title="Change Password">
        <form onSubmit={handleChangePassword} className="space-y-4">
          {passwordResult && (
            <div
              className={`p-3 rounded-lg text-sm ${
                passwordResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <p>{passwordResult.message}</p>
                <button type="button" onClick={() => setPasswordResult(null)} className="text-current opacity-50">
                  &times;
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <Button type="submit" variant="primary" isLoading={isChangingPassword}>
            Change Password
          </Button>
        </form>
      </Card>
    </div>
  );
}
